import React, { useState, useEffect } from "react";
import { Modal, Button, ConfigProvider, Input } from "antd";

import { workspaceDisplayName } from "../../utils/workspaceDisplayName";

export const DeleteConfirmationModal = ({
  open,
  userToDelete,
  deleteType = "user",
  isDeleting = false,
  onConfirm,
  onCancel
}) => {
  // Only the permanent (hard-delete) path is gated behind typing the email —
  // it purges the account from Firestore + Auth and cannot be undone.
  const requiresTypedEmail = deleteType === "permanent";
  const targetEmail = userToDelete?.email ?? "";
  const [typedEmail, setTypedEmail] = useState("");

  // Clear the gate whenever the modal opens or the target changes, so a stale
  // match can't carry over to the next user.
  useEffect(() => {
    if (open) setTypedEmail("");
  }, [open, targetEmail]);

  const emailMatches = typedEmail.trim() === targetEmail;
  const deleteDisabled =
    isDeleting || (requiresTypedEmail && !emailMatches);

  const workspaceName = workspaceDisplayName(userToDelete?.client);
  // A "workspace" removal only strips THIS membership; the user keeps their
  // other grants and stays active there. Surfacing the remaining count stops
  // the removal from reading as "the user disappeared".
  const otherWorkspaceCount = Math.max((userToDelete?.workspaceCount ?? 1) - 1, 0);
  const otherWorkspacesPhrase =
    otherWorkspaceCount === 1
      ? "1 other workspace"
      : `${otherWorkspaceCount} other workspaces`;

  // Name the action by what it actually does, not by the button that opened it.
  // Only the single-workspace case is a reversible deactivation; stripping one
  // workspace of several is permanent (the backend deletes the grant key and
  // offers no restore), so it must not borrow the word "Deactivate".
  const actionLabel =
    deleteType === "permanent"
      ? "Delete Permanently"
      : deleteType === "workspace"
        ? "Remove from Workspace"
        : "Deactivate";

  return (
    <ConfigProvider
      theme={{
        components: {
          Modal: {
            borderRadiusLG: 24,
          },
        },
      }}
    >
      <Modal
        title={
          <span style={{ fontFamily: "Inter", fontSize: "20px", fontWeight: 700, color: "#18181b" }}>
            {requiresTypedEmail ? "Delete" : actionLabel}
          </span>
        }
        open={open}
        onCancel={onCancel}
        centered
        width={480}
        styles={{
          content: {
            borderRadius: "24px",
            padding: "24px 26px",
          },
          header: {
            padding: 0,
            marginBottom: "24px",
            borderBottom: "none",
          },
          body: {
            padding: 0,
          },
          footer: {
            padding: 0,
            marginTop: "24px",
            borderTop: "none",
          },
        }}
        footer={[
          <Button
            key="cancel"
            type="text"
            onClick={onCancel}
            disabled={isDeleting}
            style={{
              fontFamily: "Inter",
              fontSize: "14px",
              fontWeight: 500,
              color: "#3f3f46",
              height: "36px",
              paddingInline: "16px",
              borderRadius: "9999px",
            }}
          >
            Cancel
          </Button>,
          <Button
            key="delete"
            onClick={onConfirm}
            loading={isDeleting}
            disabled={deleteDisabled}
            style={{
              fontFamily: "Inter",
              fontSize: "14px",
              fontWeight: 500,
              color: "#fff",
              backgroundColor: deleteDisabled ? "#f0a8a8" : "#df0101",
              border: "none",
              height: "36px",
              paddingInline: "24px",
              borderRadius: "9999px",
              cursor: deleteDisabled ? "not-allowed" : "pointer",
            }}
          >
            {actionLabel}
          </Button>,
        ]}
      >
        {userToDelete && (
          <div style={{ fontFamily: "Inter", fontSize: "14px", fontWeight: 400, color: "#18181b", lineHeight: "22px" }}>
            {deleteType === "permanent" ? (
              <>
                Permanently delete <strong>{userToDelete.name}</strong> from{" "}
                <strong>DashONE</strong>? Their account and all associated data
                will be removed from Firestore and Firebase Authentication (a
                copy is archived for audit) and the team will be notified. This
                action cannot be undone.
                <div
                  style={{
                    marginTop: "12px",
                    padding: "10px 12px",
                    background: "#eef4ff",
                    border: "1px solid #c7d9f6",
                    borderRadius: "10px",
                    fontSize: "13px",
                    color: "#123a7a",
                  }}
                >
                  Prefer <strong>Deactivate</strong>? It revokes access
                  immediately but keeps the account — reversible anytime from the
                  Deactivated view.
                </div>
                <div style={{ marginTop: "14px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "12px",
                      color: "#71717a",
                      marginBottom: "4px",
                    }}
                  >
                    Type <strong>{targetEmail}</strong> to confirm
                  </label>
                  <Input
                    value={typedEmail}
                    onChange={(e) => setTypedEmail(e.target.value)}
                    placeholder={targetEmail}
                    aria-label="Type the email to confirm permanent deletion"
                    status={typedEmail && !emailMatches ? "error" : undefined}
                    onPressEnter={() => {
                      if (emailMatches && !isDeleting) onConfirm?.();
                    }}
                  />
                </div>
              </>
            ) : deleteType === "user" ? (
              <>
                <strong>{userToDelete.name}</strong> only has access to the{" "}
                <strong>{workspaceName}</strong> workspace. Deactivating
                this user will remove their access immediately, but you can
                restore them anytime from the Deactivated view. This is
                reversible.
              </>
            ) : (
              <>
                Are you sure you want to remove{" "}
                <strong>{userToDelete.name}</strong> from the{" "}
                <strong>{workspaceName}</strong> workspace? This will
                permanently remove their access to this workspace.
                {otherWorkspaceCount > 0 && (
                  <>
                    {" "}
                    They'll keep access to their{" "}
                    <strong>{otherWorkspacesPhrase}</strong> and stay active
                    there — this does not deactivate their account.
                  </>
                )}
              </>
            )}
          </div>
        )}
      </Modal>
    </ConfigProvider>
  );
};
